/**
 * InvoicePreview Component
 * Displays the invoice with live PDF preview
 * showActions prop controls whether action buttons are displayed
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { InvoicePDFPreview } from './InvoicePDFPreview';
import { exportPDFWithReactPDF } from './PDFPreview';
import { InvoiceEditor } from './InvoiceEditor';
import { calculatePageLayouts } from '@/utils/pagination';
import { logger } from '@/utils/logger';
import { generateInvoiceFilename } from '@/utils/filename';

interface InvoicePreviewProps {
  showActions?: boolean;
  onExportComplete?: () => void;
}

export function InvoicePreview({ showActions = true, onExportComplete }: InvoicePreviewProps) {
  const { t } = useTranslation();
  const { currentInvoice, isGenerating, setExporting, totalPages } = useInvoiceStore();
  const [showEditor, setShowEditor] = useState(false);

  // Use invoice's paperSize or global default
  const effectivePaperSize = currentInvoice?.paperSize || { preset: 'A4' as const, width: 210, height: 297, orientation: 'portrait' as const };

  const handleExport = async (format: 'pdf' | 'png') => {
    if (!currentInvoice) return;

    try {
      setExporting(true);

      const filename = generateInvoiceFilename(currentInvoice);

      console.log('[handleExport] Using pdfmake export:', {
        totalPages,
        format,
      });

      // Calculate page layouts for pdfmake
      const pageLayouts = calculatePageLayouts(currentInvoice, effectivePaperSize, totalPages);

      if (format === 'pdf') {
        // Use @react-pdf/renderer for PDF export
        await exportPDFWithReactPDF(currentInvoice, effectivePaperSize, pageLayouts, filename);
      } else {
        // PNG export - note: this will export from the old HTML template
        // You may want to remove this option or update it
        alert('PNG export is not available with PDF preview mode. Please use PDF export.');
        return;
      }

      onExportComplete?.();
    } catch (error) {
      logger.error('Export failed:', error);
      alert(error instanceof Error ? error.message : t('export.failed'));
    } finally {
      setExporting(false);
    }
  };

  if (isGenerating) {
    return (
      <div className="card dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg
            className="animate-spin h-12 w-12 text-primary-600 dark:text-primary-400 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('invoice.form.generatingButton')}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {t('invoice.preview.empty')}
          </p>
        </div>
      </div>
    );
  }

  if (!currentInvoice) {
    return (
      <>
        <div className="card dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <svg
              className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('invoice.preview.title')}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {t('invoice.preview.empty')}
            </p>
          </div>
        </div>

        {/* Quick Guide Section */}
        <div className="card dark:bg-gray-800 dark:border-gray-700 p-6 mt-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Quick Guide: What is an Invoice?
          </h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            An invoice is a formal document sent by a business to a client requesting payment for goods delivered or services provided. It serves as a legal record of the transaction and helps you get paid faster.
          </p>

          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">1. Unique Invoice Number</h4>
              <p className="text-xs text-gray-700 dark:text-gray-300">A unique identifier for tracking and bookkeeping</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">2. Date Issued & Due Date</h4>
              <p className="text-xs text-gray-700 dark:text-gray-300">Clearly state when payment is expected</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">3. Business & Client Details</h4>
              <p className="text-xs text-gray-700 dark:text-gray-300">Contact information for both parties</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">4. Line Items & Totals</h4>
              <p className="text-xs text-gray-700 dark:text-gray-300">Detailed description with quantities and prices</p>
            </div>
          </div>

          <a href="/guide" className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium text-sm">
            Read complete guide
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* Inline Ad at the bottom when no invoice TODO: enable later*/}
        {/* <div className="card dark:bg-gray-800 dark:border-gray-700 p-3">
          <InlineAd />
        </div> */}
      </>
    );
  }

  const isExporting = useInvoiceStore.getState().isExporting;

  return (
    <>
      <div className="space-y-6">
        {showActions && (
          <>
            {/* Action Buttons */}
            <div className="card dark:bg-gray-800 dark:border-gray-700">
              <h3 className="card-header dark:text-gray-100">{t('export.actions')}</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowEditor(!showEditor)}
                  className="btn-secondary dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 flex items-center gap-2 flex-1 min-w-[140px]"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  {showEditor ? t('export.hideEditor') : t('export.editInvoice')}
                </button>

                <button
                  onClick={() => handleExport('pdf')}
                  disabled={isExporting}
                  className="btn-primary flex items-center gap-2 flex-1 min-w-[140px]"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {t('export.exportPDF')}
                </button>
              </div>
            </div>

            {/* Invoice Editor */}
            {showEditor && (
              <div className="card dark:bg-gray-800 dark:border-gray-700">
                <h3 className="card-header dark:text-gray-100">{t('export.editInvoice')}</h3>
                <InvoiceEditor invoice={currentInvoice} />
              </div>
            )}
          </>
        )}

        {/* Invoice PDF Preview */}
        <InvoicePDFPreview
          invoice={currentInvoice}
          paperSize={effectivePaperSize}
          pageLayouts={calculatePageLayouts(currentInvoice, effectivePaperSize, totalPages)}
        />
      </div>
    </>
  );
}
